import { useGetProviderConfig, useUpsertProviderConfig } from "@/hooks/useProviderConfig"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useApp } from "../../../AppProvider"
import { Spinner } from "../../common"
import { ErrorMessage } from "../../common/Feedback/ErrorMessage"

interface IdexxConfigFormProps {
  orgId: string
}

interface IdexxConfig {
  pimsId: string
  username: string
  password: string
}

export const IdexxConfigForm = ({ orgId }: IdexxConfigFormProps) => {
  const { token, addToast } = useApp()

  const { data: initialConfig, isLoading, isError, error } = useGetProviderConfig(orgId, "IDEXX", token)

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    reset,
  } = useForm<IdexxConfig>({
    defaultValues: {
      pimsId: initialConfig?.config?.pimsId || "",
      username: initialConfig?.config?.username || "",
      password: initialConfig?.config?.password || "",
    },
  })

  useEffect(() => {
    if (initialConfig?.config) {
      reset({
        pimsId: initialConfig.config.pimsId || "",
        username: initialConfig.config.username || "",
        password: initialConfig.config.password || "",
      })
    }
  }, [initialConfig, reset])

  const mutation = useUpsertProviderConfig(orgId, "IDEXX", token)

  const onSubmit = handleSubmit(async (data) => {
    const res = await mutation.mutateAsync({
      provider: "IDEXX",
      config: data,
    })
    if (res.data) {
      addToast("Success", "Idexx config saved", "success")
    } else {
      addToast("Error", "Failed to save config", "error")
    }
  })

  if (isLoading) return <Spinner />

  if (isError && !error.message.includes("not found")) return <ErrorMessage message={error?.message} />

  return (
    <div>
      <h3 className="text-md font-semibold text-gray-800">Idexx</h3>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">PIMS ID</label>
          <input className="input" {...register("pimsId", { required: "PIMS ID is required" })} />
          {errors.pimsId && <div className="error-message">{errors.pimsId.message}</div>}
        </div>
        <div>
          <label className="label">Username</label>
          <input className="input" {...register("username", { required: "Username is required" })} />
          {errors.username && <div className="error-message">{errors.username.message}</div>}
        </div>
        <div>
          <label className="label">Password</label>
          <input
            className="input"
            type="password"
            {...register("password", { required: "Password is required" })}
          />
          {errors.password && <div className="error-message">{errors.password.message}</div>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-secondary"
            disabled={!isDirty}
            onClick={() =>
              reset({
                pimsId: initialConfig?.config?.pimsId || "",
                username: initialConfig?.config?.username || "",
                password: initialConfig?.config?.password || "",
              })
            }
          >
            Cancel
          </button>

          <button className="btn" type="submit" disabled={!isDirty || isSubmitting || mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  )
}
